import React, {createContext, ReactNode, useContext, useMemo} from "react";

type TaskFileShareContextValue = {
  /**
   * `publish` — the authenticated console: task files are `public: false`, so
   * the link is only worth copying after `task/file/:fileId/share` flips them.
   * `public` — the anonymous email-review page: `email-review/verify` already
   * published everything it renders (the reviewer has no session to
   * authenticate a redirect with), and the share endpoint is session-gated
   * anyway, so the URL is copied as-is.
   */
  mode: "publish" | "public";
  /** Required by the share endpoint; `publish` mode only. */
  showId?: string;
};

const TaskFileShareContext = createContext<TaskFileShareContextValue | null>(
  null
);

/**
 * Marks a subtree as a place where task file links may be copied. Surfaces
 * that mount no provider (public grid preview) render no copy affordance at
 * all.
 *
 * A context rather than a prop because the attachment chips sit up to five
 * levels below (grid → rows → cell → file input → chip).
 */
export const TaskFileShareProvider = ({
  showId,
  mode = "publish",
  children,
}: {
  showId?: string;
  mode?: "publish" | "public";
  children: ReactNode;
}) => {
  const value = useMemo<TaskFileShareContextValue | null>(() => {
    if (mode === "public") return {mode};
    return showId ? {mode: "publish", showId} : null;
  }, [mode, showId]);

  return (
    <TaskFileShareContext.Provider value={value}>
      {children}
    </TaskFileShareContext.Provider>
  );
};

/** `null` outside a provider — the caller must render no share affordance. */
export const useTaskFileShare = () => useContext(TaskFileShareContext);
